import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking"); // checking | paid | pending | failed | cancelled
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (!active) return;
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status) || data.status === "failed") { setState("failed"); return; }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current > 20) { if (active) setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId, cancelled]);

  const isEmployer = info?.account_type === "employer";
  const isSub = info?.kind === "subscription";
  const grantedText = isSub
    ? "Ditt abonnemang är aktivt – du kan nu skapa obegränsat antal CV"
    : isEmployer
      ? `${info?.searches || 1} sökning${(info?.searches || 1) === 1 ? "" : "ar"} har lagts till`
      : `${info?.cv_credits || 0} CV har lagts till på ditt konto`;
  const ctaTo = isEmployer ? "/foretag" : "/app/skapa-cv";
  const ctaText = isEmployer ? "Sök kandidater" : "Skapa CV";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="payment-result-page">
      <Card className="rounded-2xl border-border p-8 max-w-md w-full text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
            <h1 className="font-head text-xl font-semibold">Bekräftar din betalning …</h1>
            <p className="text-sm text-muted-foreground mt-2">Ett litet ögonblick, vi aktiverar ditt köp.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="font-head text-2xl font-semibold">Tack för din betalning!</h1>
            <p className="text-muted-foreground mt-2">{info?.plan_name ? `${info.plan_name} · ` : ""}{grantedText}.</p>
            <Link to={ctaTo}><Button className="rounded-full mt-6" data-testid="payment-cta">{ctaText} <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
            {info?.receipt_url && (
              <div className="mt-4">
                <a href={info.receipt_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline" data-testid="payment-receipt-link">Visa kvitto</a>
              </div>
            )}
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="h-10 w-10 text-primary animate-spin mx-auto mb-4" />
            <h1 className="font-head text-xl font-semibold">Betalningen behandlas</h1>
            <p className="text-sm text-muted-foreground mt-2">Vissa betalsätt (t.ex. Klarna) kan ta en stund. Dina krediter läggs till automatiskt när betalningen är klar – du får ett kvitto via e-post.</p>
            <Link to="/app"><Button variant="outline" className="rounded-full mt-6" data-testid="payment-home">Till översikten</Button></Link>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="font-head text-xl font-semibold">Betalningen gick inte igenom</h1>
            <p className="text-sm text-muted-foreground mt-2">Inget har dragits. Du kan försöka igen.</p>
            <Link to="/app/skapa-cv"><Button variant="outline" className="rounded-full mt-6" data-testid="payment-retry">Tillbaka</Button></Link>
          </div>
        )}
        {state === "cancelled" && (
          <div data-testid="payment-cancelled">
            <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="font-head text-xl font-semibold">Betalningen avbröts</h1>
            <p className="text-sm text-muted-foreground mt-2">Inget har dragits. Du kan prova igen när du vill.</p>
            <Link to="/app"><Button variant="outline" className="rounded-full mt-6" data-testid="payment-back">Till översikten</Button></Link>
          </div>
        )}
      </Card>
    </div>
  );
}
